import axios from 'axios';

// Credentials travel as query parameters, not headers, and that is deliberate.
//
// axios.get takes (url, config); the original passed headers as a third
// argument, which axios ignores, so these requests never carried headers at all.
// Sending them properly turned every call into a cross-origin request with
// non-safelisted header names, which a browser preflights with OPTIONS. The
// older endpoints answer no OPTIONS request and send no
// Access-Control-Allow-Headers, so the preflight failed, the request was never
// made, and the app hung on "Loading ...." forever.
//
// Until those endpoints handle preflight, parameters are the only channel that
// works. api/device/claim is the exception -- it handles OPTIONS -- so postJson
// below may use headers.
// The signed-in user's credentials, added to every request centrally.
//
// The API used to be authenticated by REACT_APP_SESSION_ID: one hardcoded string
// compiled into this bundle and sent by every user for every device. The server
// never checked it -- and could not have, meaningfully, since it identifies
// nobody. The server now authenticates the user and checks that the device being
// addressed actually belongs to them, so every call has to carry the real
// credential rather than the shared constant.
//
// Read from storage rather than passed in, so that the existing call sites keep
// working unchanged. A signed-out caller sends nothing and the server refuses it.
const STORAGE_USER_KEY = 'app_user';

// The API answering with a web page instead of a body.
//
// The host runs Imunify360 WebShield. When it greylists a visitor's address it
// stops proxying to PHP and serves a JavaScript challenge page in its place --
// with HTTP 200, so every status check passes and every parse fails. Neither
// axios nor the Pi's kiosk can execute that challenge, so a request that looks
// successful returns markup where JSON was expected.
//
// This is worth naming rather than folding into a generic failure. The login
// screen reported "please enter valid user/password" for it, which sends
// someone to re-type a password that was never the problem, and the settings
// screens showed nothing at all.
export const SERVER_BLOCKED_MESSAGE =
    'The server is temporarily blocking requests from your connection. Please wait a few minutes and try again.';

export const looksBlocked = (data) =>
    typeof data === 'string' && /^\s*<(!doctype|html)/i.test(data);


const withCredentials = (params = {}) => {
    try {
        const saved = localStorage.getItem(STORAGE_USER_KEY);
        if (!saved) return params;

        const user = JSON.parse(saved);
        if (!user || !user.id || user.id === '0' || !user.auth_key) return params;

        // An explicit value already on the request wins, so a caller that means
        // to act as somebody else still can.
        return { USER_ID: user.id, AUTH_KEY: user.auth_key, ...params };
    } catch (error) {
        // Unparseable or unavailable storage is a signed-out request, not a crash.
        return params;
    }
}

export const getData = async (url, params = {}) => {
    try {
        const response = await axios.get(url, { params: withCredentials(params) });
        if (looksBlocked(response.data)) {
            console.log('Blocked by the host, not the API >', url);
            return undefined;
        }
        return response.data?.data;
    } catch (error) {
        if (looksBlocked(error.response?.data)) {
            console.log('Blocked by the host, not the API >', url);
            return undefined;
        }
        console.log('Error Getting Data >>', error, url, params);
        return undefined;
    }
}

// The whole response body, not the `data` inside it.
//
// getData exists for the older endpoints, whose bodies are
// {row_count, message, error, data}, and it unwraps that last field. Newer
// endpoints answer with their own shape and have no `data` member, so getData
// hands back undefined for every one of them -- which looks exactly like an
// empty result and reports itself as nothing being wrong.
export const getJson = async (url, params = {}) => {
    try {
        const response = await axios.get(url, { params: withCredentials(params) });
        if (looksBlocked(response.data)) {
            console.log('Blocked by the host, not the API >', url);
            return { ok: false, status: response.status, blocked: true, data: { error: SERVER_BLOCKED_MESSAGE } };
        }
        return { ok: true, status: response.status, data: response.data };
    } catch (error) {
        const status = error.response?.status ?? 0;
        if (looksBlocked(error.response?.data)) {
            return { ok: false, status, blocked: true, data: { error: SERVER_BLOCKED_MESSAGE } };
        }
        if (!error.response) {
            console.log('Network error in get request > ', error, url);
        }
        return { ok: false, status, data: error.response?.data ?? {} };
    }
}

// Was never awaited and always resolved to undefined, so callers could not tell
// a saved setting from a failed one. It now waits for the request; the contract
// of returning undefined on failure is kept so existing callers, which do not
// catch, keep behaving as they do today.
export const postData = async (url, bodyData = {}, extraHeaders = {}) => {
    const bodyFormData = new FormData();

    for (let [key, value] of Object.entries(bodyData)) {
        bodyFormData.append(`${key}`, `${value}`);
    }

    try {
        // multipart/form-data is a CORS-safelisted content type and the
        // credentials ride in the query string, so this is not preflighted.
        // Adding custom headers here would break it exactly as it broke getData.
        return await axios.post(url, bodyFormData, { params: withCredentials(extraHeaders) });
    } catch (error) {
        console.log('Error in post request > ', error);
        return undefined;
    }
}

// JSON POST that reports what happened. New code should use this: the caller
// needs to tell "wrong code" from "network down" to say anything useful.
export const postJson = async (url, body = {}, headers = {}) => {
    try {
        const response = await axios.post(url, body, {
            headers: { 'Content-Type': 'application/json', ...headers },
        });
        if (looksBlocked(response.data)) {
            console.log('Blocked by the host, not the API >', url);
            return { ok: false, status: response.status, blocked: true, data: { error: SERVER_BLOCKED_MESSAGE } };
        }
        return { ok: true, status: response.status, data: response.data };
    } catch (error) {
        const status = error.response?.status ?? 0;
        const data = error.response?.data ?? {};
        if (looksBlocked(data)) {
            return { ok: false, status, blocked: true, data: { error: SERVER_BLOCKED_MESSAGE } };
        }
        if (!error.response) {
            console.log('Network error in post request > ', error, url);
        }
        return { ok: false, status, data };
    }
}

export const getCountries = () => {
    return ([{id: 0, description: 'Canada'}, {id: 1, description: 'Egypt'}, ])
}

export const getCities = () => {
    return ([{id: 0, description: 'Edmonton'}, {id: 1, description: 'Cairo'}, ])
}
